// Big maps sent to the GPU a band of rows a frame.
//
// three uploads a map whole, in the frame that first draws it: an 8192 x 4096
// map held the main thread for 150 to 425 ms and a 4096 x 2048 one for 60 to
// 110 ms, so a drag that met one stopped for it. Here the file's bytes are
// decoded off the main thread into an ImageBitmap (flipped in the decode, as
// WebGL ignores the upload's flip for one; a canvas is copied into one, a
// model's bitmap goes as it is), three allocates a texture with the map's
// parameters and no pixels, and the rows go up BAND_BYTES a frame, about
// 0.5 ms of main thread each (more in one frame stalls on the browser's
// transfer buffer, 20 to 30 ms a frame on an M-series Mac). The mips come
// from the generateMipmap three calls. Only when the whole map is on the GPU
// does the texture take it: the two trade GPU textures and sources, so
// three's own bookkeeping holds and nothing is drawn half filled. The texels
// are three's upload's, byte for byte.
//
// The files download side by side; the maps go up one at a time, the first
// in line whose bytes have arrived, so one band goes up a frame and a map
// still downloading holds up none behind it. Where a step is not possible (no WebGL 2, a map three
// uploads another way, a browser that does not flip a bitmap or take a
// sub-rectangle of one) the call resolves false and the caller leaves the
// texture to three, as before.
import * as THREE from 'three';
import { bitmapFlips } from './bitmaptexture.js';

const BAND_BYTES = 2 * 1024 * 1024;
const BITMAP_OPTIONS = { imageOrientation: 'flipY', premultiplyAlpha: 'none', colorSpaceConversion: 'none' };

let probe = null;
let defaultRenderer = null;
const jobs = [];
let running = false;

// The file each image loaded through loadImage came from, and its bytes until
// they have gone to the GPU, so the bitmap is decoded from the same download
// (a second fetch is a cache hit only where the file is cached; the dev
// server has it fetched again).
const files = new WeakMap();

// A map to be sent in bands, downloaded once: an <img> on the file's bytes
// (decoded, as CPU readers of a texture's image expect) whose bytes
// uploadInBands then decodes for the GPU.
export async function loadImage(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  const blob = await res.blob();
  const image = document.createElement('img');
  const objectUrl = URL.createObjectURL(blob);
  try {
    image.src = objectUrl;
    await image.decode();
  } finally {
    // The loaded <img> keeps its own copy of the bytes.
    URL.revokeObjectURL(objectUrl);
  }
  files.set(image, { url: new URL(url, window.location.href).href, blob });
  return image;
}

// Lets go of the bytes loadImage kept for an image that will not be sent in
// bands (a map made into a canvas).
export function dropImageBytes(image) {
  const file = image && files.get(image);
  if (file) file.blob = null;
}

// The image's file decoded off the main thread into a bitmap with the
// pixels drawImage gives for the <img> itself, for a 2D canvas that draws
// it: a canvas decodes an <img> again on the main thread when first read
// (34 to 50 ms for a 4096 x 2048 map). Null where the bytes are gone or the
// decode fails; the caller then draws the <img>.
export async function decodedCopy(image) {
  const file = image && files.get(image);
  if (!file || !file.blob || typeof createImageBitmap !== 'function') return null;
  try {
    const bitmap = await createImageBitmap(file.blob);
    if (bitmap.width === image.naturalWidth && bitmap.height === image.naturalHeight) return bitmap;
    bitmap.close();
  } catch (_) {
    // The <img> is drawn.
  }
  return null;
}

// The address an image was loaded from (an http one, not its blob: one).
export function imageFileUrl(image) {
  const file = image && files.get(image);
  if (file) return file.url;
  const src = image && (image.currentSrc || image.src);
  return /^https?:/.test(src || '') ? src : null;
}

// What went up in bands ({ url, ms } from asked to drawn) and what was left
// to three ({ url }), for checks (window.__debug.bandUploads).
export const bandUploadLog = { sent: [], left: [] };

// main.js turns it on for the interactive viewer; until then every call
// resolves false. The check runs now, behind the loading screen, since it
// reads a pixel back.
export function enableBandUploads(renderer) {
  defaultRenderer = renderer;
  if (!probe) probe = bitmapFlips().then((flips) => flips && bandsWork(renderer)).catch(() => false);
}

// Whether maps are being sent in bands, for modules that load their own
// (they then load through loadImage).
export function bandUploadsOn() {
  return Boolean(probe) && !globalThis.__bandUploadsOff;
}

// texture draws image once the whole of it is on the GPU: its own image (a
// map no frame has drawn yet, loaded from url, a canvas made from one or a
// model's decoded bitmap) or a larger map loaded from url in place of the one
// it holds (a boot preview). Resolves true once it does. renderer null is the
// viewer's; first puts the map ahead of those waiting (one that other work
// waits for).
// window.__bandUploadsOff = true (before the page loads) leaves every map to
// three, for before/after checks.
export function uploadInBands(renderer, texture, url, image = texture && texture.image, { first = false } = {}) {
  if (!bandUploadsOn() || !texture || !image) return Promise.resolve(false);
  return new Promise((resolve) => {
    const job = { renderer: renderer || defaultRenderer, texture, url, image, resolve, asked: performance.now(), blob: null, ready: false };
    // The bytes are got now, alongside the maps going up, so one still
    // downloading never holds up those behind it.
    probe.then((works) => (works && suitable(job.renderer, texture, image, url) ? bytesOf(url, image) : null))
      .then((blob) => { job.blob = blob; }, () => {})
      .then(() => {
        job.ready = true;
        if (wake) wake();
      });
    if (first) jobs.unshift(job);
    else jobs.push(job);
    pump();
  });
}

let wake = null;

async function pump() {
  if (running) return;
  running = true;
  while (jobs.length) {
    // The first in line whose bytes are on hand.
    const job = jobs.find((j) => j.ready);
    if (!job) {
      await new Promise((resolve) => { wake = resolve; });
      wake = null;
      continue;
    }
    jobs.splice(jobs.indexOf(job), 1);
    let ok = false;
    job.timing = { started: performance.now() };
    try {
      ok = await upload(job);
    } catch (err) {
      console.warn('[textures] band upload failed for', job.url, err);
    }
    job.blob = null;
    const url = job.url ? String(job.url).replace(/^https?:\/\/[^/]+/, '') : `bitmap ${job.texture.name}`;
    const t = job.timing;
    if (ok === true) {
      bandUploadLog.sent.push({
        url, asked: Math.round(job.asked), ms: Math.round(performance.now() - job.asked), waited: Math.round(t.started - job.asked),
        decode: Math.round((t.decoded || t.started) - t.started), fill: Math.round(performance.now() - (t.decoded || t.started)), bands: t.bands || 0,
      });
    } else {
      bandUploadLog.left.push({ url });
    }
    job.resolve(ok === true);
  }
  running = false;
}

// The file's bytes: kept by loadImage, or fetched again, which the HTTP cache
// answers for the site's immutable maps. A canvas or a bitmap needs none.
async function bytesOf(url, image) {
  if (image.tagName === 'CANVAS' || isBitmap(image)) return null;
  const file = files.get(image);
  if (file && file.blob) {
    const blob = file.blob;
    file.blob = null;
    return blob;
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

async function upload({ renderer, texture, url, image, blob, timing }) {
  const props = renderer.properties;
  const onGpu = () => props.get(texture).__webglInit !== undefined;
  // Drawn meanwhile: three has sent it whole.
  if (texture.image === image && onGpu()) return true;
  if (!(await probe) || !suitable(renderer, texture, image, url)) return false;
  const bitmap = await decode(image, blob);
  if (!bitmap) return false;
  timing.decoded = performance.now();
  const source = texture.source;
  let disposed = false;
  const onDispose = () => { disposed = true; };
  texture.addEventListener('dispose', onDispose);
  const stage = texture.clone();
  stage.source = new THREE.Source(image);
  try {
    withoutUploads(renderer, ['texSubImage2D', 'generateMipmap'], () => renderer.initTexture(stage));
    if (!(await fill(renderer, stage, bitmap, timing))) return false;
    // Let go, given another map or other parameters meanwhile: left to three.
    if (disposed || texture.source !== source || CACHE_KEY.some((key) => texture[key] !== stage[key])) return false;
    if (texture.image === image && onGpu()) return true;
    if (onGpu()) {
      // Its GPU texture is keyed by parameters it no longer has.
      if (props.get(texture).__cacheKey !== props.get(stage).__cacheKey) return false;
    } else {
      // Never drawn: three takes it on with an empty texture (keyed as the
      // stage is, the parameters being the same), which the stage takes away.
      withoutUploads(renderer, ['texStorage2D', 'texImage2D', 'texSubImage2D', 'generateMipmap'], () => renderer.initTexture(texture));
    }
    const a = props.get(texture), b = props.get(stage);
    [a.__webglTexture, b.__webglTexture] = [b.__webglTexture, a.__webglTexture];
    [texture.source, stage.source] = [stage.source, texture.source];
    return true;
  } finally {
    texture.removeEventListener('dispose', onDispose);
    stage.dispose();
    if (bitmap !== image) bitmap.close();
  }
}

// The parameters three keys a GPU texture by (getTextureCacheKey).
const CACHE_KEY = ['wrapS', 'wrapT', 'wrapR', 'magFilter', 'minFilter', 'anisotropy', 'internalFormat', 'format', 'type',
  'generateMipmaps', 'premultiplyAlpha', 'flipY', 'unpackAlignment', 'colorSpace'];

// A plain texture of RGBA bytes that three would send from this very file
// (or this canvas) with no colour conversion (its rule for sRGB and linear
// maps against the sRGB working space) into WebGL 2 storage, one level that
// generateMipmap completes.
function suitable(renderer, texture, image, url) {
  if (!renderer || !renderer.capabilities.isWebGL2) return false;
  const kind = Object.getPrototypeOf(texture);
  if (kind === THREE.Texture.prototype && isBitmap(image)) {
    // A decoded bitmap (GLTFLoader's) is sent as it is; WebGL ignores the
    // flip and premultiply settings for one, as three's upload does.
    if (!image.width || !image.height) return false;
  } else {
    if (kind === THREE.CanvasTexture.prototype) {
      if (image.tagName !== 'CANVAS' || !image.width || !image.height) return false;
    } else if (kind === THREE.Texture.prototype) {
      if (image.tagName !== 'IMG' || !image.complete || !image.naturalWidth) return false;
      if (!url || imageFileUrl(image) !== new URL(url, window.location.href).href) return false;
    } else {
      return false;
    }
    // Decoded flipped and unpremultiplied, as three sends a file or canvas.
    if (texture.flipY !== true || texture.premultiplyAlpha !== false) return false;
  }
  return texture.format === THREE.RGBAFormat && texture.type === THREE.UnsignedByteType && texture.internalFormat === null
    && texture.mipmaps.length === 0 && !texture.onUpdate
    && [THREE.NoColorSpace, THREE.SRGBColorSpace, THREE.LinearSRGBColorSpace].includes(texture.colorSpace)
    && Math.max(imageWidth(image), imageHeight(image)) <= renderer.capabilities.maxTextureSize;
}

const isBitmap = (image) => typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap;
const imageWidth = (image) => image.naturalWidth || image.width;
const imageHeight = (image) => image.naturalHeight || image.height;

// The bytes (or the canvas) decoded off the main thread and flipped as
// three's upload flips; a bitmap is sent as it is.
async function decode(image, blob) {
  if (isBitmap(image)) return image;
  try {
    const bitmap = await createImageBitmap(image.tagName === 'CANVAS' ? image : blob, BITMAP_OPTIONS);
    if (bitmap.width === imageWidth(image) && bitmap.height === imageHeight(image)) return bitmap;
    bitmap.close();
  } catch (_) {
    // Left to three.
  }
  return null;
}

// Runs three with the named calls left out, so it does its bookkeeping and
// sets the parameters without sending or allocating what they would.
function withoutUploads(renderer, names, run) {
  const gl = renderer.getContext();
  const kept = names.map((name) => [name, Object.prototype.hasOwnProperty.call(gl, name), gl[name]]);
  for (const name of names) gl[name] = () => {};
  try {
    run();
  } finally {
    for (const [name, own, fn] of kept) {
      if (own) gl[name] = fn;
      else delete gl[name];
    }
  }
}

// One band a frame, then the mips. False if the context went meanwhile.
async function fill(renderer, texture, bitmap, timing = {}) {
  const gl = renderer.getContext();
  const { width, height } = bitmap;
  const rows = Math.max(1, Math.floor(BAND_BYTES / (width * 4)));
  for (let y = 0; y < height; y += rows) {
    await nextFrame();
    const glTexture = renderer.properties.get(texture).__webglTexture;
    if (gl.isContextLost() || !glTexture) return false;
    // The bitmap is flipped, so its row y is the texture's row y.
    subImage(renderer, glTexture, () => {
      gl.pixelStorei(gl.UNPACK_SKIP_ROWS, y);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, y, width, Math.min(rows, height - y), gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
    });
    timing.bands = (timing.bands || 0) + 1;
  }
  const glTexture = renderer.properties.get(texture).__webglTexture;
  if (gl.isContextLost() || !glTexture) return false;
  if (texture.generateMipmaps && texture.minFilter !== THREE.NearestFilter && texture.minFilter !== THREE.LinearFilter) {
    renderer.state.bindTexture(gl.TEXTURE_2D, glTexture);
    gl.generateMipmap(gl.TEXTURE_2D);
    renderer.state.unbindTexture();
  }
  return true;
}

// The next frame, or a tenth of a second where a headset's session drives the
// frames and the page's own may stop.
function nextFrame() {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, 100);
    requestAnimationFrame(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

// Binds glTexture through three's state and sets the unpack state an upload
// from a bitmap takes; three sets its own before each of its uploads, and the
// sub-rectangle ones are put back.
function subImage(renderer, glTexture, send) {
  const gl = renderer.getContext();
  const kept = [gl.UNPACK_ROW_LENGTH, gl.UNPACK_SKIP_PIXELS, gl.UNPACK_SKIP_ROWS].map((p) => [p, gl.getParameter(p)]);
  renderer.state.bindTexture(gl.TEXTURE_2D, glTexture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
  gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
  gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
  try {
    send();
  } finally {
    for (const [p, v] of kept) gl.pixelStorei(p, v);
    renderer.state.unbindTexture();
  }
}

// Whether this browser takes a sub-rectangle of a flipped bitmap where asked:
// a 1 x 4 bitmap sent as two bands of two rows into a texture of its own and
// read back, on the renderer's context with its bindings put back.
async function bandsWork(renderer) {
  if (!renderer.capabilities.isWebGL2 || typeof createImageBitmap !== 'function' || typeof fetch !== 'function') return false;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 4;
  const ctx = canvas.getContext('2d');
  ['#ff0000', '#00ff00', '#0000ff', '#ffffff'].forEach((colour, i) => {
    ctx.fillStyle = colour;
    ctx.fillRect(0, i, 1, 1);
  });
  const bitmap = await createImageBitmap(canvas, BITMAP_OPTIONS);
  const gl = renderer.getContext();
  const texture = gl.createTexture();
  const framebuffer = gl.createFramebuffer();
  const read = gl.getParameter(gl.READ_FRAMEBUFFER_BINDING);
  const draw = gl.getParameter(gl.DRAW_FRAMEBUFFER_BINDING);
  const px = new Uint8Array(16);
  try {
    renderer.state.bindTexture(gl.TEXTURE_2D, texture);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, 1, 4);
    renderer.state.unbindTexture();
    for (const y of [0, 2]) {
      subImage(renderer, texture, () => {
        gl.pixelStorei(gl.UNPACK_SKIP_ROWS, y);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, y, 1, 2, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
      });
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.readPixels(0, 0, 1, 4, gl.RGBA, gl.UNSIGNED_BYTE, px);
  } finally {
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, read);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, draw);
    gl.deleteFramebuffer(framebuffer);
    gl.deleteTexture(texture);
    bitmap.close();
  }
  // Bottom row first: the canvas's last row (white) up to its first (red).
  const want = [[255, 255, 255], [0, 0, 255], [0, 255, 0], [255, 0, 0]];
  return want.every((rgb, row) => rgb.every((v, c) => Math.abs(px[row * 4 + c] - v) <= 1));
}
